import React from 'react';
import styles from './gift-line.module.css';
import { InfoBtn } from '../InfoBtn';
import { useViewModel } from './useViewModel';

/**
 * Gift bag component for selecting and customizing gift wrap options.
 * @param {Object} props - Component props.
 * @param {string} props.imageUrl - The image URL for the gift item.
 * @param {string} props.title - The title of the gift item.
 * @param {number} props.price - The price of the gift item.
 * @param {string} props.description - The description of the gift item.
 * @param {string} props.parentId - The parent ID of the gift item.
 * @param {string|null} [props.variantId=null] - The variant ID if this is a variant.
 * @param {boolean} [props.isExpanded=false] - Whether the item is expanded.
 * @param {Function} [props.onExpand] - Callback for expansion.
 * @param {Function} [props.onVariantClick] - Callback for variant selection.
 */
export function GiftLine({
  imageUrl,
  title,
  price,
  description,
  parentId,
  variantId = null,
  isExpanded = false,
  onExpand,
  onVariantClick
}) {
  const { handleClickGiftInfo, handleClick, formatPrice, isSelected } = useViewModel({
    parentId,
    imageUrl,
    title,
    description,
    price,
    variantId
  });

  return (
    <div
      className={`${styles.giftLine} ${isSelected ? styles['giftLine--selected'] : ''} ${isExpanded ? styles['giftLine--expanded'] : ''}`}
    >
      {isSelected && (
        <span className={styles['giftLine__checkmark']}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 3L4.5 9L2 6.5"
              stroke="var(--color-white)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      <div className={styles.giftLine__image}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} />
        ) : (
          <div className={styles.giftLine__imagePlaceholder}>
            {/* Gift bag icon placeholder */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ color: 'var(--color-grey)' }}
            >
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          </div>
        )}
      </div>

      <div
        className={styles.giftLine__content}
        onClick={(e) => {
          // If this is a variant, use the variant click handler
          if (variantId && onVariantClick) {
            onVariantClick(e);
            return;
          }
          // If this is the main item and has an expand handler, use it
          if (!variantId && onExpand) {
            onExpand();
            return;
          }
          // Otherwise use the default click handler
          handleClick();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // If this is a variant, use the variant click handler
            if (variantId && onVariantClick) {
              onVariantClick(e);
              return;
            }
            // If this is the main item and has an expand handler, use it
            if (!variantId && onExpand) {
              onExpand();
              return;
            }
            // Otherwise use the default click handler
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        <h3 className={styles.giftLine__title}>{title}</h3>
        <p className={styles.giftLine__price}>{formatPrice({ amount: price })}</p>
      </div>

      <div className={styles.giftLine__actions}>
        <InfoBtn onClick={handleClickGiftInfo} aria-label="Show gift info" />
      </div>
    </div>
  );
}

export default GiftLine;
